/**
 * Client-Side Analytics Service
 * 
 * Handles analytics tracking from client components via server actions
 */

import { AnalyticsPayload } from './analytics';

/**
 * Send analytics data to server for processing
 */
async function sendToServer(payload: AnalyticsPayload): Promise<void> {
  // Headers fixed: x-forwarded-host now matches host (getjar.com)
  
  try {
    // Import server action dynamically to avoid bundling server code
    const { sendImpressionWithPayload } = await import('./serverImpressions');
    
    // Convert payload to server impressions format
    await sendImpressionWithPayload({
      event: payload.event,
      userId: payload.userId,
      userClass: payload.userClass,  
      anonymousId: payload.anonymousId,
      url: payload.url,
      path: payload.path,
      title: payload.title,
      referrer: payload.referrer,
      user_agent: payload.userAgent,
      countryCode: payload.countryCode,
      ...payload.properties,
    });
  } catch (error) {
    console.warn('Server analytics tracking failed:', error);
  }
}

/**
 * Track Google Analytics directly on client
 */
function trackGoogleAnalytics(payload: AnalyticsPayload): void {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  try {
    const gaId = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;
    if (!gaId) return;

    if (payload.event === 'PAGE_VIEW') {
      window.gtag('event', 'page_view', {
        page_path: payload.path || payload.url,
        page_title: payload.title,
        send_to: gaId,
        ...payload.properties,
      });
    } else {
      window.gtag('event', payload.event, {
        ...payload.properties,
        send_to: gaId,
      });
    }
  } catch (error) {
    console.warn('Google Analytics tracking failed:', error);
  }
}

/**
 * Client-side analytics tracking
 */
export async function trackClientEvent(payload: AnalyticsPayload): Promise<void> {
  // Track on Google Analytics immediately (client-side)
  trackGoogleAnalytics(payload);
  
  // Send to server for RudderStack processing
  await sendToServer(payload);
}

/**
 * Track page view from client components
 */
export async function trackClientPageView(payload: Omit<AnalyticsPayload, 'event'>): Promise<void> {
  await trackClientEvent({
    ...payload,
    event: 'PAGE_VIEW',
  });
}